/** Matches store listing multer (jpeg, jpg, png, webp) and 1 MB limit. */

export const IMAGE_MAX_BYTES = 1024 * 1024;

/** `accept` attribute for file inputs */
export const IMAGE_ACCEPT_ATTR =
  "image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp";

/** Shown under upload inputs (1080×1080 is a recommendation only). */
export const IMAGE_TYPES_HELPER_TEXT =
  "JPG, JPEG, PNG, or WebP only — max 1 MB per file. For the best look in the UI, use images around 1080×1080 px (1:1 square); other sizes still upload.";

const ALLOWED_IMAGE_MIME = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

export function isAllowedImageMime(type) {
  if (!type) return false;
  const t = String(type).toLowerCase();
  return ALLOWED_IMAGE_MIME.has(t);
}

/**
 * @param {File} file
 * @returns {Promise<{ ok: true, file: File } | { ok: false, message: string }>}
 */
export function validateImageFileForUpload(file) {
  return new Promise((resolve) => {
    if (!(file instanceof File)) {
      resolve({ ok: false, message: "Invalid file." });
      return;
    }
    if (!isAllowedImageMime(file.type)) {
      resolve({
        ok: false,
        message: "Invalid file type. Use JPG, JPEG, PNG, or WebP.",
      });
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      resolve({
        ok: false,
        message: "Image size is more than 1 MB. Please upload an image of 1 MB or smaller.",
      });
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ ok: true, file });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        ok: false,
        message: "Could not read this image. Try another file.",
      });
    };
    img.src = url;
  });
}

/**
 * @param {FileList|File[]} fileList
 * @param {(message: string) => void} [onReject]
 * @returns {Promise<File[]>}
 */
export async function filterValidImageFiles(fileList, onReject) {
  const out = [];
  for (const file of Array.from(fileList || [])) {
    const r = await validateImageFileForUpload(file);
    if (r.ok) out.push(r.file);
    else {
      const label = file?.name ? `${file.name}: ${r.message}` : r.message;
      onReject?.(label);
    }
  }
  return out;
}
